import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import isEqual from 'react-fast-compare';
import {connect} from 'react-redux';

import {trackEvent} from 'analytics/Analytics';
import {bulkArchiveAssayRuns, bulkUnarchiveAssayRuns} from 'archive/ArchiveActions';
import {ALL_ARCHIVED_PURPOSE_TEXT} from 'archive/ArchiveConstants';
import ArchiveButton from 'archive/components/buttons/ArchiveButton';
import UnarchiveButton from 'archive/components/buttons/UnarchiveButton';
import {RESULTS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {BenchlingState} from 'redux-stores/reduceApp';
import {BenchlingDispatch} from 'redux-stores/Store';
import {AssaySchema} from 'results/assay-schemas/AssaySchemaConstants';
import {AssayRun} from 'results/assays/AssayRecordConstants';
import {MODEL_NAMES} from 'results/ResultsNamingConstants';
import {getEmptyFilterErrors} from 'search/filters/ValidateFilters';
import {Filter, FilterKey, SingleValueFilter} from 'search/SearchConstants';
import {sanitizeFilters} from 'search/SearchUtil';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Modal from 'ui-library/modals/Modal';

import RunsInboxFilters from './RunsInboxFilters';
import {AvailabilityStatus} from './RunsInboxModalConstants';
import {fetchAssayRuns, resetState} from './RunsInboxModalReduxActions';
import RunsInboxTable from './RunsInboxTable';

interface ConnectedProps {
  isLoading: boolean;
  selectedRun: AssayRun | null;
  assayResultSchemas: Array<AssaySchema> | null;
  filters: Array<Filter>;
}

function mapStateToProps(state: BenchlingState, props: BaseProps): ConnectedProps {
  const {isLoading, selectedRunId, filters} = state.ui.results.runsInboxModal;
  const {assayRunsById, assayResultSchemasByRunId} = state.models.results;
  const assayResultSchemas =
    assayResultSchemasByRunId == null || selectedRunId == null
      ? null
      : assayResultSchemasByRunId[selectedRunId];
  const selectedRun = selectedRunId ? (assayRunsById[selectedRunId] as AssayRun) : null;
  return {
    isLoading,
    selectedRun,
    assayResultSchemas,
    filters,
  };
}

interface DispatchProps {
  onFetchAssayRuns: (schemaId: string, filters: Array<Filter>) => void;
  onResetState: () => void;
  onArchive: (runId: string, purpose: string) => void;
  onUnarchive: (runId: string) => void;
}

function mapDispatchToProps(dispatch: BenchlingDispatch): DispatchProps {
  return {
    onFetchAssayRuns: (schemaId: string, filters: Array<Filter>) => {
      dispatch(fetchAssayRuns(schemaId, filters));
    },
    onResetState: () => {
      dispatch(resetState());
    },
    onArchive: (runId: string, purpose: string) => {
      dispatch(bulkArchiveAssayRuns([runId], purpose));
    },
    onUnarchive: (runId: string) => {
      dispatch(bulkUnarchiveAssayRuns([runId]));
    },
  };
}

interface ArchiveOrUnarchiveButtonProps {
  forArchive: boolean;
  disabled: boolean;
  onArchive: () => void;
  onUnarchive: () => void;
}
function ArchiveOrUnarchiveButton(props: ArchiveOrUnarchiveButtonProps): JSX.Element {
  const sharedProps = {
    disabled: props.disabled,
    buttonStyle: ButtonStyle.TERTIARY_GRAY,
  };
  if (props.forArchive) {
    return (
      <ArchiveButton
        {...sharedProps}
        tooltipTitle={`Archive ${MODEL_NAMES.run}`}
        onClick={() => {
          props.onArchive();
          trackEvent(RESULTS_ANALYTICS_ACTIONS.ARCHIVED_RUN_FROM_INBOX);
        }}
      />
    );
  } else {
    return (
      <UnarchiveButton
        {...sharedProps}
        tooltipTitle={`Unarchive ${MODEL_NAMES.run}`}
        onClick={() => {
          props.onUnarchive();
          trackEvent(RESULTS_ANALYTICS_ACTIONS.UNARCHIVED_RUN_FROM_INBOX);
        }}
      />
    );
  }
}

interface BaseProps {
  assayRunSchema: AssaySchema;
  entryId: string;
  onInsertAssayRun: (selectedRunId: string, assayResultSchemas: Array<AssaySchema>) => void;
  onRequestClose: () => void;
}

export type RunsInboxModalProps = BaseProps & ConnectedProps & DispatchProps;

export class RunsInboxModal extends React.Component<RunsInboxModalProps> {
  override componentDidMount(): void {
    this.props.onFetchAssayRuns(this.props.assayRunSchema.id, this.props.filters);
  }

  override componentWillUnmount(): void {
    this.props.onResetState();
  }

  override componentDidUpdate(prevProps: RunsInboxModalProps): void {
    const prevFilters = sanitizeFilters(prevProps.filters, getEmptyFilterErrors(prevProps.filters));
    const currFilters = sanitizeFilters(this.props.filters, getEmptyFilterErrors(this.props.filters));
    if (!isEqual(prevFilters, currFilters)) {
      this.props.onFetchAssayRuns(this.props.assayRunSchema.id, currFilters);
    }
  }

  _getStatusFilter(): AvailabilityStatus {
    return (
      this.props.filters.find((filter) => filter.key === FilterKey.RUN_AVAILABILITY) as SingleValueFilter
    ).value as AvailabilityStatus;
  }

  _shouldDisableInsertRun(): boolean {
    const selectedRun = this.props.selectedRun;
    return (
      selectedRun == null ||
      this.props.assayResultSchemas == null ||
      selectedRun.archiveRecord != null ||
      !selectedRun.permissions.writable ||
      this._getStatusFilter() === AvailabilityStatus.IN_ENTRY
    );
  }

  override render(): JSX.Element {
    const statusFilter = (
      this.props.filters.find((filter) => filter.key === FilterKey.RUN_AVAILABILITY) as SingleValueFilter
    ).value as AvailabilityStatus;

    return (
      <Modal
        buttons={[
          {
            text: `Insert ${MODEL_NAMES.run}`,
            onClick: () => {
              const runToInsert = this.props.selectedRun!;
              trackEvent(RESULTS_ANALYTICS_ACTIONS.INSERTED_RUN_FROM_INBOX, {
                run_id: runToInsert.id,
                run_api_id: runToInsert.apiId,
                run_schema_api_id: this.props.assayRunSchema.id,
                origin_type: runToInsert.originType,
                entry_id: this.props.entryId,
              });
              this.props.onInsertAssayRun(runToInsert.id, this.props.assayResultSchemas!);
              this.props.onResetState();
            },
            disabled: this._shouldDisableInsertRun(),
            style: ButtonStyle.SECONDARY,
            dataTestAttrs: {component: 'RunsInboxModal', key: ModalButtonKey.INSERT},
          },
        ]}
        closeableOnEscapeKey={false}
        large={true}
        footer={`${MODEL_NAMES.Results} linked to these ${MODEL_NAMES.runs} will also be inserted`}
        onRequestClose={this.props.onRequestClose}
        footerPullRight={true}
        title={`${MODEL_NAMES.Runs} inbox`}
      >
        <div>
          <div className={css(styles.toolbar)}>
            <RunsInboxFilters assayRunSchema={this.props.assayRunSchema} />
            <ArchiveOrUnarchiveButton
              forArchive={this.props.selectedRun?.archiveRecord == null}
              disabled={this.props.selectedRun == null || statusFilter === AvailabilityStatus.IN_ENTRY}
              onArchive={() => {
                this.props.onArchive(this.props.selectedRun!.id, ALL_ARCHIVED_PURPOSE_TEXT);
              }}
              onUnarchive={() => {
                this.props.onUnarchive(this.props.selectedRun!.id);
              }}
            />
          </div>
          {this.props.isLoading ? (
            <CenteredHorizontalThrobber />
          ) : (
            <RunsInboxTable assayRunSchema={this.props.assayRunSchema} />
          )}
        </div>
      </Modal>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(RunsInboxModal);

const styles = StyleSheet.create({
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    marginRight: 2,
  },
});
